'use client'

import { useMemo } from 'react'

import { CheckIcon, ChevronsUpDownIcon, Settings2Icon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'

import { cn } from '@/lib/cn'
import { useDataTableContext } from './data-table-root'

export function DataTableViewOptions() {
  const { table } = useDataTableContext()
  const columns = useMemo(() => {
    const c = table
      .getAllColumns()
      .filter(
        (column) =>
          typeof column.accessorFn !== 'undefined' && column.getCanHide()
      )

    return c.map((column) => {
      const headerAsLabel =
        typeof column.columnDef.header === 'string'
          ? column.columnDef.header
          : null

      return {
        column,
        label: column.columnDef.meta?.label || headerAsLabel || column.id,
      }
    })
  }, [table])

  const reset = () => {
    table.resetColumnVisibility()
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label="Toggle columns"
          role="combobox"
          variant="outline"
          size="sm"
          className="ml-auto h-8 lg:flex"
        >
          <Settings2Icon />
          View
          <ChevronsUpDownIcon className="ml-auto opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 p-0">
        <Command>
          <CommandInput placeholder="Search columns..." />
          <CommandList>
            <CommandEmpty>No columns found.</CommandEmpty>
            <CommandGroup>
              {columns.map(({ column, label }) => (
                <CommandItem
                  key={column.id}
                  onSelect={() =>
                    column.toggleVisibility(!column.getIsVisible())
                  }
                >
                  <span className="truncate">{label}</span>
                  <CheckIcon
                    className={cn(
                      'ml-auto size-4 shrink-0',
                      column.getIsVisible() ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <Separator />
        <Button
          variant="ghost"
          size="sm"
          className="w-full rounded-t-none"
          onClick={reset}
        >
          Reset
        </Button>
      </PopoverContent>
    </Popover>
  )
}
